// bd-deepcontext-hfv7u.6 — pure windowing/merge helpers for the runs modal's
// windowed history + minimap. Kept separate from JobRunsModal so the math is
// unit-testable without React.
import { ScheduledJobRun } from "./types";

// How many runs fill one window of the chart (the visible page). The agreed
// design (bd-hfv7u.10) shows the latest 15 runs at a time; the cursor endpoint
// pages in larger chunks so a single fetched page usually covers an Older step.
export const WINDOW_SIZE = 15;

/** Timestamp used to order runs chronologically (oldest → newest). */
export function runTimestamp(run: ScheduledJobRun): number {
  const t = Date.parse(run.startedAt ?? run.createdAt);
  return Number.isFinite(t) ? t : 0;
}

/**
 * Merge two run sets (live SSE set + fetched older pages), dedupe by id, and
 * sort oldest → newest. Later-seen fields win on conflict so live SSE updates
 * (status/progress) overwrite a stale fetched copy of the same run.
 */
export function mergeRuns(
  primary: ScheduledJobRun[],
  incoming: ScheduledJobRun[],
): ScheduledJobRun[] {
  const byId = new Map<string, ScheduledJobRun>();
  for (const r of incoming) byId.set(r.id, r);
  // primary (live) overrides incoming (fetched) for the same id.
  for (const r of primary) byId.set(r.id, r);
  return Array.from(byId.values()).sort(
    (a, b) => runTimestamp(a) - runTimestamp(b),
  );
}

export interface HistoryWindow {
  start: number; // inclusive index into the chronological list
  end: number; // exclusive index
}

/**
 * Clamp a desired window [start, start+size) to the bounds of a list of `total`
 * runs. Returns a valid non-negative range; for an empty list it is [0, 0].
 */
export function clampWindow(
  desiredStart: number,
  size: number,
  total: number,
): HistoryWindow {
  if (total <= 0) return { start: 0, end: 0 };
  const w = Math.min(size, total);
  let start = Math.max(0, Math.min(desiredStart, total - w));
  if (!Number.isFinite(start)) start = total - w;
  return { start, end: start + w };
}

/** The window that shows the most-recent `size` runs. */
export function latestWindowStart(size: number, total: number): number {
  return Math.max(0, total - size);
}

/** True when the window's right edge is at the newest run. */
export function isAtLatest(win: HistoryWindow, total: number): boolean {
  return total === 0 || win.end >= total;
}

/** True when the window's left edge is at the oldest loaded run. */
export function isAtOldestLoaded(win: HistoryWindow): boolean {
  return win.start <= 0;
}

/** Format a run timestamp as a short calendar date, e.g. "Jun 24". */
export function formatRunDate(ts: number): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
}

/**
 * Date range label for a chronological slice of runs, e.g. "Jun 24 – Jun 30".
 * Collapses to a single date when the slice spans one day. Empty slice → "".
 */
export function formatDateRange(runs: ScheduledJobRun[]): string {
  if (runs.length === 0) return "";
  const first = formatRunDate(runTimestamp(runs[0]));
  const last = formatRunDate(runTimestamp(runs[runs.length - 1]));
  return first === last ? first : `${first} – ${last}`;
}
